import { savePushSubscription, type PushClientId } from "./pushSubscriptionsApi";
import { registerPushServiceWorker } from "./serviceWorkerUpdate";
import { getVapidPublicKey, isWebPushSupported, urlBase64ToUint8Array } from "./vapid";

export type RegisterWebPushResult =
  | { ok: true }
  | { ok: false; reason: "unsupported" | "missing_vapid" | "denied" | "error" };

export type RegisterWebPushOptions = {
  token: string;
  buildingId: number;
  client?: PushClientId;
  requestPermission?: boolean;
};

/**
 * Enregistre le service worker, crée l'abonnement push et le persiste via l'API.
 */
export async function registerWebPush(
  options: RegisterWebPushOptions,
): Promise<RegisterWebPushResult> {
  const { token, buildingId, client = "accueilv2", requestPermission = true } = options;

  if (!isWebPushSupported()) {
    return { ok: false, reason: "unsupported" };
  }

  const vapidPublicKey = getVapidPublicKey();
  if (!vapidPublicKey) {
    return { ok: false, reason: "missing_vapid" };
  }

  if (Notification.permission === "denied") {
    return { ok: false, reason: "denied" };
  }

  if (requestPermission && Notification.permission === "default") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return { ok: false, reason: "denied" };
    }
  }

  if (Notification.permission !== "granted") {
    return { ok: false, reason: "denied" };
  }

  try {
    const registration = await registerPushServiceWorker();
    if (!registration) {
      return { ok: false, reason: "unsupported" };
    }
    await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
    }

    await savePushSubscription(token, buildingId, subscription.toJSON(), client);
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
}
